<script setup lang="ts">
import { computed, ref } from 'vue'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'

const PocketButton = UIKit.Button
const PocketInput = UIKit.Input
const ModalContainer = UIKit.ModalContainer

defineProps<{
    orgName: string
}>()

const emit = defineEmits<{
    close: []
}>()

const firstName = ref('')
const lastName = ref('')
const errorFields = ref<string[]>([])
const isLoading = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const saveName = async () => {
    isLoading.value = true
    errorFields.value = []

    if (!firstName.value) {
        errorFields.value.push('firstName')
    }
    if (!lastName.value) {
        errorFields.value.push('lastName')
    }

    if (!errorFields.value.length) {
        await userModule.actions.updateUser({
            firstName: firstName.value,
            lastName: lastName.value,
        })

        emit('close')
    }

    isLoading.value = false
}
</script>

<template>
    <Portal to="modal">
        <ModalContainer key="nameModal" :is-dark-mode="isDarkMode">
            <div class="name-modal">
                <div class="name-modal__title">
                    Complete your profile
                </div>
                <div class="name-modal__text">
                    You’ve been added to {{ orgName }}. Complete your profile so {{ orgName }} knows who you are.
                </div>
                <PocketInput
                    v-model="firstName"
                    :is-dark-mode="isDarkMode"
                    label="First Name"
                    :error="errorFields.includes('firstName')"
                    class="name-modal__input"
                    @keydown.enter="saveName"
                />
                <PocketInput
                    v-model="lastName"
                    :is-dark-mode="isDarkMode"
                    label="Last Name"
                    :error="errorFields.includes('lastName')"
                    class="name-modal__input"
                    @keydown.enter="saveName"
                />
                <PocketButton
                    :is-dark-mode="isDarkMode"
                    :disabled="!firstName || !lastName"
                    class="name-modal__submit"
                    :is-loading="isLoading"
                    @click="saveName"
                >
                    Save
                </PocketButton>
            </div>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.name-modal {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 35px 42px 26px 39px;
    width: 374px;
    margin: 0 auto;

    &__title {
        font-weight: 600;
        font-size: 18px;
        line-height: 22px;
        margin-bottom: 12px;
    }

    &__text {
        color: $slate-03;
        font-size: 15px;
        line-height: 22px;
        text-align: center;
        margin-bottom: 29px;

        &--dark {
            color: $white;
        }
    }

    &__input {
        margin-bottom: 20px;
        width: 324px;
    }

    &__submit {
        margin-top: 13px;
    }
}
</style>
